// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer, { CartLineContainerStyles } from "@eisinc/App/Components/Cart/CartLineContainer";
import { CertificationBadges } from "@eisinc/App/Components/CertificationBadges/CertificationBadges";
import ProductImagesBadges, {
    ProductImagesBadgesStyles,
} from "@eisinc/App/Components/CustomBadges/ProductImagesBadges";
import QtyField from "@eisinc/App/Components/Qty/QtyField";
import Button from "@eisinc/MobiusOverrides/Button";
import ProductBrand, { ProductBrandStyles } from "@eisinc/Overrides/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@eisinc/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@eisinc/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@eisinc/Overrides/Components/ProductPrice";
import { cartLineCardExpandedStyles } from "@eisinc/Overrides/Widgets/Cart/CartLineCardExpanded";
import CartLineNotes from "@eisinc/Overrides/Widgets/Cart/CartLineNotes";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import setRequisitionIsExpanded from "@insite/client-framework/Store/Pages/Requisitions/Handlers/SetRequisitionIsExpanded";
import setRequisitionIsSelected from "@insite/client-framework/Store/Pages/Requisitions/Handlers/SetRequisitionIsSelected";
import translate from "@insite/client-framework/Translate";
import { RequisitionModel } from "@insite/client-framework/Types/ApiModels";
import ProductAvailability from "@insite/content-library/Components/ProductAvailability";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import RequisitionsRequisitionLinesTable from "@insite/content-library/Widgets/Requisitions/RequisitionsRequisitionLinesTable";
import { AccordionPresentationProps } from "@insite/mobius/Accordion";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    requisition: RequisitionModel;
    availability?: any;
    isInStock?: boolean;
    isPartial?: boolean;
    isBackOrder?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.requisitions.selectedRequisitionIds.find(o => o === ownProps.requisition.id),
    isExpanded: !!state.pages.requisitions.expandedRequisitionIds.find(o => o === ownProps.requisition.id),
    productSettings: getSettingsCollection(state).productSettings,
    session: state.context.session,
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    setRequisitionIsSelected,
    setRequisitionIsExpanded,
    addToWishList,
    setAddToListModalIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RequisitionsProductListLineStyles {
    mainContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    infoGridItem?: GridItemProps;
    infoContainer?: GridContainerProps;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    partNumbersGridItem?: GridItemProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityGridItem?: GridItemProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    subtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    subtotalHeadingAndText?: SmallHeadingAndTextStyles;
    requisitionLinesGridItem?: GridItemProps;
    requisitionLinesAccordion?: AccordionPresentationProps;
    viewRequisitionLink?: LinkPresentationProps;
    smallHeadingText?: SmallHeadingAndTextStyles;
    // XNG-Change
    gridContainer?: CartLineContainerStyles;
    certificationsStyles?: InjectableCss;
    certBadgesWrapper?: InjectableCss;
    eisBadge?: InjectableCss;
    mfrBadge?: InjectableCss;
    addToListLink?: LinkPresentationProps;
    productImage?: ProductImageStyles;
    badgesWrapper?: InjectableCss;
    productBrand?: ProductBrandStyles;
    productInfoGridItem?: InjectableCss;
    productPartNumbersGridItem?: InjectableCss;
    productAvailabilityWrapper?: InjectableCss;
    productImagesBadgesStyles?: ProductImagesBadgesStyles;
}

export const requisitionsProductListLineStyles: RequisitionsProductListLineStyles = {
    mainContainer: {
        gap: 10,
        css: css`
            padding: 20px 0;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    imageGridItem: {
        width: [3, 3, 2, 2, 2],
    },
    selectedCheckbox: {
        css: css`
            margin-right: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            min-width: 0;
            flex-direction: column;
        `,
    },
    infoGridItem: {
        width: [9, 9, 10, 10, 10],
    },
    infoContainer: {
        gap: 10,
    },
    descriptionGridItem: {
        width: [12, 12, 5, 6, 6],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    partNumbersGridItem: { width: 12 },
    productPriceStyles: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            size: 10,
            weight: "bold",
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
    },
    priceGridItem: { width: [4, 4, 2, 2, 2] },
    quantityGridItem: { width: [4, 4, 2, 2, 2] },
    quantityHeadingAndText: {
        heading: {
            size: 10,
            weight: "bold",
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
    },
    subtotalGridItem: {
        width: [4, 4, 2, 2, 2],
        css: css`
            flex-direction: column;
        `,
    },
    subtotalWithoutVatHeadingAndText: {
        heading: {
            size: 10,
            weight: "bold",
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
        text: {
            weight: "bold",
        },
        wrapper: {
            css: css`
                margin-bottom: 10px;
            `,
        },
    },
    subtotalHeadingAndText: {
        heading: {
            size: 10,
            weight: "bold",
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
        text: {
            weight: "bold",
        },
    },
    requisitionLinesGridItem: {
        width: 12,
    },
    requisitionLinesAccordion: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = requisitionsProductListLineStyles;

const RequisitionsProductListLine = ({
    requisition,
    isSelected,
    isExpanded,
    productSettings,
    setRequisitionIsSelected,
    setRequisitionIsExpanded,
    session,
    wishListSettings,
    addToWishList,
    setAddToListModalIsOpen,
    enableVat,
    vatPriceDisplay,
    showPricing,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [eisCertificate, setEisCertificate] = React.useState(
        requisition.properties["eis/GCGCertification"] === "true",
    );
    const [mfrCertificate, setMfrCertificate] = React.useState(requisition.properties["mfrCertification"] === "true");
    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setRequisitionIsSelected({ requisitionId: requisition.id, isSelected: value });
    };

    const togglePanelHandler = () => {
        setRequisitionIsExpanded({ requisitionId: requisition.id, isExpanded: !isExpanded });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: requisition.productId!,
            qtyOrdered: requisition.qtyOrdered!,
            unitOfMeasure: requisition.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const handleModalClose = () => {
        setRequisitionIsExpanded({ requisitionId: requisition.id, isExpanded: !isExpanded });
    };

    const badges = requisition?.properties["badges"] ? JSON.parse(requisition?.properties["badges"]) : null;

    return (
        <>
            <CartLineContainer
                extendedStyles={styles.gridContainer}
                data-test-selector={`requisition_${requisition.productId}_${requisition.unitOfMeasure}`}
            >
                <CartLineContainer.Image>
                    <Checkbox {...styles.selectedCheckbox} checked={isSelected} onChange={selectChangeHandler}>
                        {translate("Select Line")}
                    </Checkbox>
                    <ProductImage product={requisition} extendedStyles={styles.productImage} />

                    <CertificationBadges hasEisCertification={eisCertificate} hasMfrCertification={mfrCertificate} />
                </CartLineContainer.Image>
                <CartLineContainer.Description>
                    <StyledWrapper {...styles.productInfoGridItem}>
                        <ProductDescription product={requisition} extendedStyles={styles.productDescriptionStyles} />
                        <StyledWrapper {...styles.productPartNumbersGridItem}>
                            <ProductPartNumbers
                                showLabel={true}
                                showSpecNumber={true}
                                showCustomerName={true}
                                showManufacturerItem={true}
                                showProductNumber={true}
                                productNumber={requisition.erpNumber}
                                customerProductNumber={requisition.customerName}
                                manufacturerItem={requisition.manufacturerItem}
                                specNumber={requisition.properties["specNumber"] || ""}
                                extendedStyles={cartLineCardExpandedStyles.productPartNumbers}
                            />
                            {requisition.brand && (
                                <ProductBrand
                                    showLabel={true}
                                    brand={requisition.brand}
                                    extendedStyles={styles.productBrand}
                                />
                            )}
                        </StyledWrapper>

                        {!requisition.quoteRequired && (
                            <>
                                <StyledWrapper {...styles.badgesWrapper}>
                                    {badges && (
                                        <ProductImagesBadges
                                            product={requisition}
                                            badges={badges}
                                            extendedStyles={styles.productImagesBadgesStyles}
                                        />
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.productAvailabilityWrapper}>
                                    <ProductAvailability
                                        productId={requisition.productId!}
                                        availability={requisition.availability!}
                                        unitOfMeasure={requisition.baseUnitOfMeasure}
                                        trackInventory={requisition.trackInventory}
                                        extendedStyles={cartLineCardExpandedStyles.productAvailability}
                                    />
                                </StyledWrapper>
                            </>
                        )}
                    </StyledWrapper>
                </CartLineContainer.Description>
                <CartLineContainer.PriceUnit>
                    <ProductPrice
                        showLabel={true}
                        product={requisition}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.productPriceStyles}
                    />
                </CartLineContainer.PriceUnit>
                <CartLineContainer.Qty>
                    <QtyField editable={false} value={requisition.qtyOrdered!} />
                </CartLineContainer.Qty>
                <CartLineContainer.Total>
                    {!requisition.quoteRequired && showPricing && (
                        <SmallHeadingAndText
                            heading={translate("Total")}
                            text={requisition.pricing!.extendedUnitNetPriceDisplay}
                            extendedStyles={styles.smallHeadingText}
                        />
                    )}
                </CartLineContainer.Total>
                <CartLineContainer.Actions>
                    <StyledWrapper {...cartLineCardExpandedStyles.wishListAndCheckboxes}>
                        <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                            {translate("Shopping List")}
                        </Link>
                        <Link {...styles.viewRequisitionLink} onClick={togglePanelHandler}>
                            {translate("View Requisitions")}
                        </Link>
                    </StyledWrapper>
                </CartLineContainer.Actions>
                <CartLineContainer.Notes>
                    {requisition.notes?.length > 0 && (
                        <CartLineNotes editable={false} extendedStyles={cartLineCardExpandedStyles.cartLineNotes} />
                    )}
                </CartLineContainer.Notes>
            </CartLineContainer>
            <Modal
                headline={translate("Viewing Requisition For:")}
                sizeVariant="large"
                isOpen={isExpanded}
                handleClose={handleModalClose}
            >
                <RequisitionsRequisitionLinesTable requisitionId={requisition.id} />
                <StyledWrapper className="modal-actions">
                    <Button variant="primary" onClick={handleModalClose}>
                        {translate("Continue")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RequisitionsProductListLine);
